"use client";

import type { ProductDetails } from "@raumagarn/formatters";
import { clsx } from "@trydig/ui";
import { Suspense, useEffect, useMemo } from "react";
import { CartNotification } from "~/components/ui/CartNotification/CartNotification";
import { RadioFieldset } from "~/components/ui/RadioFieldset";
import {
	RecipeFormat,
	useRecipeTypePicker,
} from "~/components/ui/RecipeTypePicker/useRecipeTypePicker";
import { missingYarnsCount } from "~/lib/centra/utils/missingYarnsCount";
import { useCart } from "~/lib/hooks/useCart";
import { useCurrency } from "~/lib/hooks/useCentra";
import { textconfig } from "~/textconfig";

export type RecipeTypePickerProps = {
	product: ProductDetails;
	pdfExists: boolean;
};

const UnsuspendedRecipeTypePicker = ({ product, pdfExists }: RecipeTypePickerProps) => {
	const { type, format, options, selected } = useRecipeTypePicker(product, {
		defaultValues: {
			format: pdfExists ? RecipeFormat.Digital : RecipeFormat.Paper,
		},
	});

	// Memoize the logic for detecting the magazine and "hefte" combination
	const isMagazine: boolean = useMemo(() => {
		return type?.value === "magazine" && selected?.label?.toLocaleLowerCase() === "hefte";
	}, [type, selected]);

	const { groupItems } = useCart();
	const missingYarns = missingYarnsCount(groupItems);

	// Automatically set the format to 'paper' when isMagazine is true
	useEffect(() => {
		if (
			type?.value === "magazine" &&
			selected?.label?.toLocaleLowerCase() === "hefte" &&
			format?.value !== RecipeFormat.Paper
		) {
			format?.set({
				target: {
					value: RecipeFormat.Paper,
				},
			});
		}
	}, [format, type, selected]);

	const { data: currency } = useCurrency();

	const radioFieldsetOptions = useMemo<
		{
			value: RecipeFormat;
			label: string;
			disabled?: boolean;
		}[]
	>(() => {
		// If Centra provides an explicit set of recipe types, it takes full precedence
		if (product.availableRecipeTypes && product.availableRecipeTypes.length > 0) {
			return product.availableRecipeTypes.map((recipeType) => ({
				label: recipeType.label ?? "",
				value: recipeType.value as RecipeFormat,
			}));
		}

		// If recipie has a digital version & is free and is not a magazine, only show digital option
		if (currency && product.freeRecipe?.includes(currency) && pdfExists && !isMagazine) {
			return [
				{
					label: textconfig.pdp.recipes.format.digital,
					value: RecipeFormat.Digital,
				},
			];
		}
		// For non-Norway countries, only show paper option
		if (currency !== "NOK") {
			return [
				{
					label: textconfig.pdp.recipes.format.paper,
					value: RecipeFormat.Paper,
				},
			];
		}

		// For Norway, check if we can show digital options
		const canShowDigital = pdfExists && !isMagazine;

		// Always include paper option
		const options: {
			value: RecipeFormat;
			label: string;
			disabled?: boolean;
		}[] = [
			{
				label: textconfig.pdp.recipes.format.paper,
				value: RecipeFormat.Paper,
			},
		];

		// Only add digital options if PDF exists and it's not a magazine
		if (canShowDigital) {
			options.unshift({
				label: textconfig.pdp.recipes.format.digital,
				value: RecipeFormat.Digital,
			});

			options.push({
				label: textconfig.pdp.recipes.format.multi,
				value: RecipeFormat.DigitalAndPaper,
			});
		}

		return options;
	}, [isMagazine, pdfExists, currency, product.freeRecipe, product.availableRecipeTypes]);

	// Auto-select first available format option
	useEffect(() => {
		const availableOptions = radioFieldsetOptions.filter((option) => !option.disabled);
		const currentFormatAvailable = availableOptions.some((option) => option.value === format.value);

		if (!currentFormatAvailable && availableOptions.length > 0) {
			format.set({
				target: {
					value: availableOptions[0].value,
				},
			});
		}
	}, [radioFieldsetOptions, format]);

	return (
		<div className={clsx("pdp-card ")}>
			<div className="pdp-sub-container">
				<p className="pdp-sub-container-title">Oppskrift</p>
				<p className="pdp-sub-container-label">
					<span>{selected?.label}</span>
					<span>{selected?.sku}</span>
				</p>
				<RadioFieldset name="type" large={true} options={options} state={type} />
				<CartNotification type="info" message={missingYarns.text} show={missingYarns.isMissing} />
			</div>
			<div className="pdp-sub-container">
				<RadioFieldset
					name="format"
					legend={"Format:"}
					options={radioFieldsetOptions}
					state={format}
				/>
			</div>
		</div>
	);
};

export const RecipeTypePicker: typeof UnsuspendedRecipeTypePicker = (props) => {
	return (
		<Suspense>
			<UnsuspendedRecipeTypePicker {...props} />
		</Suspense>
	);
};
